import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';
import { GRID_SIZE } from 'src/constants/gridSize';
import { SEARCH_ACTIONS, SearchRequest } from 'src/actions/SearchActions';
import {
    searchAdditionalResolve,
    searchFetch,
    extendCustomFieldsByDomains,
    searchPrimaryResolve, processItemsTitle,
} from 'src/sagas/searchFetch';
import { updateQueryString } from 'src/sagas/utils/updateQueryString';
import { scrollToTop } from 'src/sagas/utils/scrollToTop';
import { getSearchResult } from 'src/selectors/getSearchResult';
import { getIsQueryChanged } from 'src/selectors/getIsQueryChanged';
import { SearchResultItem } from '@xiag-search/sdk';
import { DEFAULT_QUERY } from 'src/constants/defaultQuery';
import { customFieldsMapping } from 'src/constants/customFieldsMapping';

interface FetchedData {
    readonly total: number;
    readonly data: SearchResultItem[];
}

export function* searchSaga({ meta: params }: SearchRequest): SagaIterator {
    try {
        yield put(SEARCH_ACTIONS.pending(params));

        const isChanged: boolean = yield select(getIsQueryChanged, params.query);
        if (!isChanged && params.query) {
            const currentResult = yield select(getSearchResult);
            yield call(scrollToTop);
            yield put(SEARCH_ACTIONS.success(currentResult, params));
            return;
        }

        const query = params.query ? params.query : DEFAULT_QUERY;
        const fetchedData: FetchedData = yield call(searchFetch, query);
        const total = fetchedData.total;
        const items = processItemsTitle(extendCustomFieldsByDomains(fetchedData.data, customFieldsMapping));
        const primaryResults = yield call(searchPrimaryResolve, items);

        yield put(SEARCH_ACTIONS.success({
            total,
            results: primaryResults,
            isFullyLoaded: total <= GRID_SIZE,
        }, params));

        if (total > GRID_SIZE) {
            const additionalResults = yield call(searchAdditionalResolve, items);
            yield put(SEARCH_ACTIONS.success({
                total,
                results: [...primaryResults, ...additionalResults],
                isFullyLoaded: true,
            }, params));
        }
        if (params.query) {
            yield call(updateQueryString, params);
        }
    } catch (error) {
        yield put(SEARCH_ACTIONS.failure(error, params));
        yield call(updateQueryString, params);
    }
}
